/**
 * The pool comparison chart, in its own panel.
 *
 * Separate from the pool modules because one screen already has a chart:
 * Аналитика draws the account's PnL curve, and giving it a second one meant
 * two charts of the same period stacked above each other. The Overview
 * composes this one beside the modules; Аналитика composes its own.
 */
import type { AnalyticsPointResponse, PoolPerformanceResponse } from "@/shared/api";
import { Button } from "@/design-system/Button";

import { PoolIndexChart, type IndexSeries } from "./PoolIndexChart";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

export function PoolIndexPanel({
  realised,
  pools,
  selected,
  onSelect,
}: {
  /** The account's realised curve — the total every pool is read against. */
  readonly realised: readonly AnalyticsPointResponse[];
  /** Each pool's own curve, so selecting one has something to select. */
  readonly pools: readonly PoolPerformanceResponse[];
  readonly selected: string | null;
  readonly onSelect: (key: string | null) => void;
}): React.JSX.Element {
  // The total, then one line per pool. Until the pools carried curves of their
  // own this chart drew a single line and selecting a pool merely greyed it —
  // a control that could not answer the question it offered.
  const series: IndexSeries[] = [
    ...(realised.length < 2
      ? []
      : [{ key: "total", label: "Skryptos общий", points: realised }]),
    ...pools
      .filter((pool) => pool.realised.length >= 2)
      .map((pool) => ({ key: pool.pool, label: pool.pool, points: pool.realised })),
  ];

  return (
    <section
      data-testid="pool-chart-panel"
      className="flex min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default xl:col-span-8"
    >
      <div className={`${PANEL_HEADER} justify-between gap-3`}>
        <h2 className={PANEL_TITLE}>
          Динамика пулов · индекс 100
        </h2>
        {selected !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSelect(null);
            }}
            className="px-1 text-action-primary hover:text-action-hover"
          >
            Показать все
          </Button>
        )}
      </div>
      {/* Grows with the row: the panel beside it lists one block per pool,
          and a fixed chart left the rest of this one empty (2026-10-08). */}
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {series.length === 0 ? (
          <p className="text-body-main text-text-muted" data-testid="pool-chart-empty">
            Кривая за период недоступна — сравнивать нечего.
          </p>
        ) : (
          <PoolIndexChart series={series} selected={selected} onSelect={onSelect} />
        )}
      </div>
    </section>
  );
}
